/**
 * The About page editor. Sections and contact are saved as one unit:
 * `updateSiteSettings` merges shallowly, so a PUT carrying only half of
 * `about` would replace the whole object and drop the other half.
 *
 * Contact inputs are sent as typed, blanks included — the server's
 * `blankToNull` reads `""` as "not set", which is why the draft holds strings
 * rather than nulls.
 */
import { useEffect, useState } from "react";
import type { AboutContact, AboutSection } from "@alpha/shared";
import { MAX_ABOUT_SECTIONS, emptyAbout } from "@alpha/shared";
import { api, apiErrorMessage } from "../../lib/api";
import { useBranding, useSiteSettings } from "../../lib/siteSettings";
import { useSiteSettingsMutation } from "../../lib/adminData";
import {
  Button,
  Card,
  ChevronRightIcon,
  Field,
  Input,
  PageHeader,
  PlusIcon,
  SectionCard,
  Textarea,
  TrashIcon,
  Workbench,
} from "../../components/ui";

type ContactDraft = Record<keyof AboutContact, string>;

const toContactDraft = (c: AboutContact): ContactDraft => ({
  email: c.email ?? "",
  phone: c.phone ?? "",
  address: c.address ?? "",
  mapsUrl: c.mapsUrl ?? "",
  facebookUrl: c.facebookUrl ?? "",
  instagramUrl: c.instagramUrl ?? "",
  xUrl: c.xUrl ?? "",
});

const CONTACT_FIELDS: {
  key: keyof ContactDraft;
  label: string;
  hint?: string;
  placeholder: string;
}[] = [
  { key: "email", label: "Email", placeholder: "info@rivercitybaseball.org" },
  { key: "phone", label: "Phone", placeholder: "(555) 123-4567" },
  { key: "address", label: "Mailing address", placeholder: "1200 River Rd, Dallas, TX 75201" },
  {
    key: "mapsUrl",
    label: "Maps link",
    hint: "Optional. Leave blank to link a map search for the address.",
    placeholder: "https://maps.app.goo.gl/…",
  },
  { key: "facebookUrl", label: "Facebook", placeholder: "https://facebook.com/yourorg" },
  { key: "instagramUrl", label: "Instagram", placeholder: "https://instagram.com/yourorg" },
  { key: "xUrl", label: "X", placeholder: "https://x.com/yourorg" },
];

export function AdminAbout() {
  const settingsQ = useSiteSettings();
  const current = useBranding();
  const seed = current.about ?? emptyAbout();

  const [sections, setSections] = useState<AboutSection[]>(seed.sections);
  const [contact, setContact] = useState<ContactDraft>(toContactDraft(seed.contact));
  const [error, setError] = useState<string>();

  // Seed once the real settings arrive — the fields start on the fallback
  // defaults, which flash briefly if content is already saved.
  useEffect(() => {
    if (!settingsQ.data) return;
    const about = settingsQ.data.about ?? emptyAbout();
    setSections(about.sections);
    setContact(toContactDraft(about.contact));
  }, [settingsQ.data]);

  const setSection = (i: number, patch: Partial<AboutSection>) =>
    setSections(sections.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  const move = (i: number, delta: number) => {
    const j = i + delta;
    if (j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setSections(next);
  };

  const save = useSiteSettingsMutation(() =>
    api.put("/admin/settings", {
      about: {
        // Both halves, always: a partial `about` replaces the whole object.
        // Half-written sections are dropped rather than 400ing the whole save.
        sections: sections
          .filter((s) => s.title.trim() && s.body.trim())
          .map((s) => ({ title: s.title.trim(), body: s.body.trim() })),
        contact,
      },
    }),
  );

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Admin"
        title="About page"
        subtitle="Tell visitors who your organization is. Everyone can read this; only admins can change it."
      />

      <form
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(undefined, {
            onSuccess: () => setError(undefined),
            onError: (err) => setError(apiErrorMessage(err, "Could not save the About page.")),
          });
        }}
      >
        <Workbench
          aside={
            <SectionCard title="Contact" description="Shown beside your sections on /about.">
              <div className="space-y-4">
                {CONTACT_FIELDS.map((f) => (
                  <Field key={f.key} label={f.label} hint={f.hint}>
                    <Input
                      value={contact[f.key]}
                      onChange={(e) => setContact({ ...contact, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                    />
                  </Field>
                ))}
              </div>
            </SectionCard>
          }
        >
          <div className="space-y-4">
            {sections.length === 0 ? (
              <p className="text-sm text-slate-400">
                No sections yet. Add one to start the page — &ldquo;Who we are&rdquo; is a good
                first.
              </p>
            ) : null}

            {sections.map((s, i) => (
              <Card key={i} className="space-y-3 p-5 sm:p-6">
                <div className="flex items-start gap-2">
                  <Input
                    value={s.title}
                    onChange={(e) => setSection(i, { title: e.target.value })}
                    placeholder="Who we are"
                    maxLength={80}
                    aria-label={`Section ${i + 1} title`}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    aria-label={`Move section ${i + 1} up`}
                    disabled={i === 0}
                    onClick={() => move(i, -1)}
                  >
                    <ChevronRightIcon className="h-4 w-4 -rotate-90" />
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    aria-label={`Move section ${i + 1} down`}
                    disabled={i === sections.length - 1}
                    onClick={() => move(i, 1)}
                  >
                    <ChevronRightIcon className="h-4 w-4 rotate-90" />
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    aria-label={`Remove section ${i + 1}`}
                    onClick={() => setSections(sections.filter((_, j) => j !== i))}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
                <Textarea
                  value={s.body}
                  onChange={(e) => setSection(i, { body: e.target.value })}
                  rows={6}
                  maxLength={2500}
                  placeholder="River City Baseball has run youth tournaments since 1998…"
                  aria-label={`Section ${i + 1} body`}
                />
                <p className="text-xs text-slate-400">{s.body.length}/2500</p>
              </Card>
            ))}

            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="secondary"
                disabled={sections.length >= MAX_ABOUT_SECTIONS}
                onClick={() => setSections([...sections, { title: "", body: "" }])}
              >
                <PlusIcon className="h-4 w-4" />
                Add section
              </Button>
              {sections.length >= MAX_ABOUT_SECTIONS ? (
                <span className="text-xs text-slate-400">
                  {MAX_ABOUT_SECTIONS} sections is the maximum.
                </span>
              ) : null}
            </div>

            {error ? <p className="text-sm text-red-600">{error}</p> : null}

            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save About page"}
            </Button>
          </div>
        </Workbench>
      </form>
    </div>
  );
}
